// src/components/Admin/Conference/Evaluation/ConferenceEvalutionChart.js
import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Button
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { saveAs } from 'file-saver'; // npm i file-saver

const ConferenceEvaluationChart = () => {
  const location = useLocation();
  const history = useHistory();
const eventname = localStorage.getItem('eventname') || 'Conference Evaluation';
  const evaluations = location.state?.evaluations || [];
  const customFields = location.state?.customFields || [];

  if (!evaluations.length || !customFields.length) {
    return <Typography>No evaluation data to display.</Typography>;
  }

  // Aggregate responses
  const aggregateResponses = () => {
    const orderGroups = {};
    customFields.forEach(f => {
      const order = f.order_by || 999;
      if (!orderGroups[order]) orderGroups[order] = [];
      orderGroups[order].push(f);
    });

    const chartData = {};

    Object.values(orderGroups).forEach(group => {
      const mainQ = group.find(f => f.type === null);
      if (!mainQ) return;

      const subQuestions = group.filter(f => f.type !== null);

      chartData[mainQ.name] = subQuestions.map(subQ => {
        const counts = {};
        const participants = {};

        evaluations.forEach(({ participant, responses }) => {
          const response = responses[subQ.name];
          if (!response) return;

          const value = response.value || "No Response";
          const name = participant?.participant_name || `ID-${participant?.id}`;

          counts[value] = (counts[value] || 0) + 1;
          participants[value] = participants[value] ? [...participants[value], name] : [name];
        });

        
        return { subQuestion: subQ.name, counts, participants };
      });
    });

    return chartData;
  };

  const chartData = aggregateResponses();

  // CSV Download
  const handleDownloadCSV = () => {
    const headers = ["Main Question", "Sub-question", "Participant Name", "Answer"];
    const rows = [];

    Object.entries(chartData).forEach(([mainQ, subQs]) => {
      subQs.forEach(subQ => {
        Object.entries(subQ.participants).forEach(([answer, names]) => {
          names.forEach(name => {
            rows.push([mainQ, subQ.subQuestion, name, answer]);
          });
        });
      });
    });

    const csvContent = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `${eventname}.csv`);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 900, margin: 'auto', mt: 10 }}>
      <Typography variant="h4" gutterBottom>
        Evaluation Summary Report
      </Typography>

      <Button
        sx={{ float: 'right', marginTop: '2%', ml:2, ":hover": { color: "white" } }}
        color='primary'
        size='small'
        variant='contained'
        onClick={handleDownloadCSV}
      >
        Download CSV
      </Button>

      <Button
        sx={{ float: 'right', marginTop: '2%', ":hover": { color: "white" } }}
        color='success'
        size='small'
        variant='contained'
        onClick={() => history.goBack()}
      >
        Back
      </Button>

      {Object.entries(chartData).map(([mainQ, subQs], mainIndex) => (
        <Box key={mainIndex} sx={{ mb: 8 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
            Main Question: {mainQ}
          </Typography>

          {subQs.length === 0 && (
            <Typography variant="body1" color="text.secondary">
              No sub-questions for this main question.
            </Typography>
          )}

          {subQs.map((subQ, subIndex) => {
            const dataForChart = Object.entries(subQ.counts).map(([answer, count]) => ({
              name: `${answer} (${count})`,
              count,
            //   participants: subQ.participants[answer]
            }));

            return (
              <Box key={subIndex} sx={{ mb: 5 }}>
                <Typography variant="h6" gutterBottom>
                  Sub-question: {subQ.subQuestion}
                </Typography>

                {dataForChart.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No responses yet.
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataForChart} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      {/* <Tooltip
                        formatter={(value, name, props) => {
                          const namesList = props.payload.participants.join(", ");
                          return [`${value}`, `Participants: ${namesList}`];
                          return [`${value}`, `Participants: ${namesList}`];
                        }}
                      /> */}
                      <Legend />
                      <Bar dataKey="count" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default ConferenceEvaluationChart;
